/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NetworkSecurityPolicyModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import {
    INetworkSecurityPolicyActionRLParam,
    INetworkSecurityRule,
    NetworkSecurityPolicyActionType,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { NetworkSecurityRule } from 'object-types';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { SchemaService } from 'ajs/modules/core/services/schema-service';
import { NetworkSecurityRuleModalComponent }
    from 'ng/modules/network-security-policy/components/network-security-rule-modal';
import { NetworkSecurityMatchTargetConfigItem }
    from './network-security-match-target.config-item.factory';

import * as l10n from '../network-security-policy.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TNetworkSecurityRule = Omit<INetworkSecurityRule, 'match' | 'rl_param'>;

/**
 * Using values from NetworkSecurityPolicyActionType enum to
 * implement checks on template.
 */
export const networkSecurityPolicyActionTypeHash = {
    NETWORK_SECURITY_POLICY_ACTION_TYPE_ALLOW:
    NetworkSecurityPolicyActionType.NETWORK_SECURITY_POLICY_ACTION_TYPE_ALLOW,
    NETWORK_SECURITY_POLICY_ACTION_TYPE_DENY:
    NetworkSecurityPolicyActionType.NETWORK_SECURITY_POLICY_ACTION_TYPE_DENY,
    NETWORK_SECURITY_POLICY_ACTION_TYPE_RATE_LIMIT:
    NetworkSecurityPolicyActionType.NETWORK_SECURITY_POLICY_ACTION_TYPE_RATE_LIMIT,
};

/**
 * Interface for NetworkSecurity Rule Partial.
 */
interface INetworkSecurityRuleConfig extends TNetworkSecurityRule {
    match?: NetworkSecurityMatchTargetConfigItem;
    rl_param?: MessageItem<INetworkSecurityPolicyActionRLParam>
}

/**
 * @description NetworkSecurityRule ConfigItem class.
 *
 * @author Abhinesh Gour
 */
export class NetworkSecurityRuleConfigItem extends
    withFullModalMixin(MessageItem)<INetworkSecurityRuleConfig> {
    public static ajsDependencies = [
        'l10nService',
        'schemaService',
    ];

    private readonly l10nService: L10nService;
    private readonly schemaService: SchemaService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: NetworkSecurityRule,
            windowElement: NetworkSecurityRuleModalComponent as Type<Component>,
            ...args,
        };

        super(extendedArgs);

        this.schemaService = this.getAjsDependency_('schemaService');
        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Looks up description field for rule's selected action return enum value if
     * description is not available.
     */
    public get ruleActionText(): string {
        const { action } = this.config;

        return this.schemaService
            .getEnumValueDescription('NetworkSecurityPolicyActionType', action) ||
            this.schemaService.getEnumValue('NetworkSecurityPolicyActionType', action).label;
    }

    /**
     * Getter for rl_param.
     */
    public get rateLimitParam(): INetworkSecurityPolicyActionRLParam {
        return this.config.rl_param.config;
    }

    /**
     * Generate Rate Limit action value text. To show in grid's expanded row panel.
     */
    public get rateLimitActionText(): string {
        const {
            burst_size: burstSize,
            max_rate: maxRate,
        } = this.rateLimitParam;

        return `${this.l10nService.getMessage(l10nKeys.burstSizeLabel)}: ${burstSize},
            ${this.l10nService.getMessage(l10nKeys.maxRateLabel)}: ${maxRate}`;
    }

    /**
     * Handles change in selection of action radio buttons.
     */
    public handleActionChange(): void {
        if (this.config.action !==
            networkSecurityPolicyActionTypeHash.NETWORK_SECURITY_POLICY_ACTION_TYPE_RATE_LIMIT) {
            delete this.rateLimitParam.burst_size;
            delete this.rateLimitParam.max_rate;
        }
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.networkSecurityRuleModalBreadcrumbTitle);
    }

    /** @override */
    // eslint-disable-next-line class-methods-use-this
    protected requiredFields(): string[] {
        return [
            'match',
            'rl_param',
        ];
    }

    /**
     * Set default value for action as ALLOW if it's not present.
     * @override
     */
    protected modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        this.config.action = this.config.action ||
            networkSecurityPolicyActionTypeHash.NETWORK_SECURITY_POLICY_ACTION_TYPE_ALLOW;
    }

    /**
     * Remove default params for ratelimit if it's not selected.
     * Set enable as false if not existing in config.
     * @override
     */
    protected modifyConfigDataBeforeSave(): void {
        super.modifyConfigDataBeforeSave();

        const { action } = this.config;

        if (action !==
            networkSecurityPolicyActionTypeHash.NETWORK_SECURITY_POLICY_ACTION_TYPE_RATE_LIMIT) {
            delete this.rateLimitParam.burst_size;
            delete this.rateLimitParam.max_rate;
        }

        this.config.enable = Boolean(this.config.enable);
    }
}
